import { createNotification } from '../../modules/notificationManager';

import {
  OPTIMIZE_ROUTES,
  OPTIMIZE_ROUTES_SUCCESS,
  OPTIMIZE_ROUTES_FAILURE,
  GET_TASK_STATUS,
  GET_TASK_STATUS_SUCCESS,
  GET_TASK_STATUS_FAILURE,
  GET_ROUTES_BY_DATE,
  GET_ROUTES_BY_DATE_SUCCESS,
  GET_ROUTES_BY_DATE_FAILURE,
  REORDER_ROUTE,
  REORDER_ROUTE_SUCCESS,
  REORDER_ROUTE_FAILURE,
  INSERT_JOB_TO_ROUTE,
  INSERT_JOB_TO_ROUTE_SUCCESS,
  INSERT_JOB_TO_ROUTE_FAILURE,
  UPDATE_JOB_LOCATION,
  UPDATE_JOB_LOCATION_SUCCESS,
  UPDATE_JOB_LOCATION_FAILURE,
  COMPARE_ROUTE_STRATEGIES,
  COMPARE_ROUTE_STRATEGIES_SUCCESS,
  COMPARE_ROUTE_STRATEGIES_FAILURE,
  LOCK_ROUTE,
  LOCK_ROUTE_SUCCESS,
  LOCK_ROUTE_FAILURE,
  DELETE_ROUTE,
  DELETE_ROUTE_SUCCESS,
  DELETE_ROUTE_FAILURE,
  OPTIMIZE_FROM_LOCATION,
  OPTIMIZE_FROM_LOCATION_SUCCESS,
  OPTIMIZE_FROM_LOCATION_FAILURE,
  CLEAR_ROUTE_OPTIMIZATION_STATE,
  SET_SELECTED_ROUTE,
  CLEAR_SELECTED_ROUTE,
} from '../../modules/constants';

const initialState = {
  routes: [],
  selectedRoute: null,
  taskStatus: null,
  comparisonData: null,
  isLoadingRoutes: false,
  isOptimizing: false,
  isReordering: false,
  isInsertingJob: false,
  isUpdatingLocation: false,
  isComparing: false,
  isLocking: false,
  isDeleting: false,
  error: null,
  optimizationResult: null,
  selectedDate: null,
};

// Handler functions
const optimizeRoutesHandler = (state) => {
  return {
    ...state,
    isOptimizing: true,
    error: null,
  };
};

const optimizeRoutesSuccessHandler = (state, action) => {
  const { data, date } = action.payload;

  createNotification('success', 'Routes optimized.', 3000);

  return {
    ...state,
    isOptimizing: false,
    optimizationResult: data,
    selectedDate: date,
    error: null,
  };
};

const optimizeRoutesFailureHandler = (state, action) => {
  const error = action.payload?.error || action.payload?.response?.data?.error || action.payload?.data?.error || 'Optimization failed';
  
  createNotification('error', error, 3000);

  return {
    ...state,
    isOptimizing: false,
    error: error,
  };
};

const getTaskStatusHandler = (state) => {
  return {
    ...state,
    error: null,
  };
};

const getTaskStatusSuccessHandler = (state, action) => {
  const { data } = action.payload;

  return {
    ...state,
    taskStatus: data,
    error: null,
  };
};

const getTaskStatusFailureHandler = (state, action) => {
  return {
    ...state,
    error: action.payload?.data?.error || 'Failed to get task status',
  };
};

const getRoutesByDateHandler = (state) => {
  return {
    ...state,
    isLoadingRoutes: true,
    error: null,
  };
};

const getRoutesByDateSuccessHandler = (state, action) => {
  const { data, date } = action.payload;

  return {
    ...state,
    isLoadingRoutes: false,
    routes: data,
    selectedDate: date,
    error: null,
  };
};

const getRoutesByDateFailureHandler = (state, action) => {
  const error = action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to fetch routes';

  createNotification('error', error, 3000);

  return {
    ...state,
    isLoadingRoutes: false,
    error: error,
  };
};

const reorderRouteHandler = (state) => {
  return {
    ...state,
    isReordering: true,
    error: null,
  };
};

const reorderRouteSuccessHandler = (state, action) => {
  const { data, route_id } = action.payload;

  createNotification('success', 'Route reordered successfully!', 3000);

  // Update the specific route in the routes array
  const updatedRoutes = state.routes.map(route =>
    route.id === route_id ? { ...route, ...data } : route
  );

  return {
    ...state,
    isReordering: false,
    routes: updatedRoutes,
    selectedRoute: state.selectedRoute?.id === route_id ? { ...state.selectedRoute, ...data } : state.selectedRoute,
    error: null,
  };
};

const reorderRouteFailureHandler = (state, action) => {
  const error = action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to reorder route';

  createNotification('error', error, 3000);

  return {
    ...state,
    isReordering: false,
    error: error,
  };
};

const insertJobToRouteHandler = (state) => {
  return {
    ...state,
    isInsertingJob: true,
    error: null,
  };
};

const insertJobToRouteSuccessHandler = (state, action) => {
  const { data, route_id } = action.payload;

  createNotification('success', 'Job inserted successfully!', 3000);

  // Update the specific route
  const updatedRoutes = state.routes.map(route =>
    route.id === route_id ? { ...route, ...data } : route
  );

  return {
    ...state,
    isInsertingJob: false,
    routes: updatedRoutes,
    selectedRoute: state.selectedRoute?.id === route_id ? { ...state.selectedRoute, ...data } : state.selectedRoute,
    error: null,
  };
};

const insertJobToRouteFailureHandler = (state, action) => {
  const error = action.payload?.error || action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to insert job';

  createNotification('error', error, 3000);

  return {
    ...state,
    isInsertingJob: false,
    error: error,
  };
};

const updateJobLocationHandler = (state) => {
  return {
    ...state,
    isUpdatingLocation: true,
    error: null,
  };
};

const updateJobLocationSuccessHandler = (state, action) => {
  createNotification('success', 'Job location updated successfully!', 3000);

  return {
    ...state,
    isUpdatingLocation: false,
    error: null,
  };
};

const updateJobLocationFailureHandler = (state, action) => {
  const error = action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to update job location';

  createNotification('error', error, 3000);

  return {
    ...state,
    isUpdatingLocation: false,
    error: error,
  };
};

const compareRouteStrategiesHandler = (state) => {
  return {
    ...state,
    isComparing: true,
    error: null,
  };
};

const compareRouteStrategiesSuccessHandler = (state, action) => {
  const { data } = action.payload;

  return {
    ...state,
    isComparing: false,
    comparisonData: data,
    error: null,
  };
};

const compareRouteStrategiesFailureHandler = (state, action) => {
  const error = action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to compare strategies';

  createNotification('error', error, 3000);

  return {
    ...state,
    isComparing: false,
    error: error,
  };
};

const lockRouteHandler = (state) => {
  return {
    ...state,
    isLocking: true,
    error: null,
  };
};

const lockRouteSuccessHandler = (state, action) => {
  const { data, route_id, locked } = action.payload;

  createNotification('success', locked ? 'Route locked successfully!' : 'Route unlocked successfully!', 3000);

  // Update the specific route
  const updatedRoutes = state.routes.map(route =>
    route.id === route_id ? { ...route, is_locked: locked } : route
  );

  return {
    ...state,
    isLocking: false,
    routes: updatedRoutes,
    selectedRoute: state.selectedRoute?.id === route_id ? { ...state.selectedRoute, is_locked: locked } : state.selectedRoute,
    error: null,
  };
};

const lockRouteFailureHandler = (state, action) => {
  const error = action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to lock/unlock route';

  createNotification('error', error, 3000);

  return {
    ...state,
    isLocking: false,
    error: error,
  };
};

const deleteRouteHandler = (state) => {
  return {
    ...state,
    isDeleting: true,
    error: null,
  };
};

const deleteRouteSuccessHandler = (state, action) => {
  const { route_id } = action.payload;

  createNotification('success', 'Route deleted successfully!', 3000);

  // Remove the deleted route from the routes array
  const updatedRoutes = state.routes.filter(route => route.id !== route_id);

  return {
    ...state,
    isDeleting: false,
    routes: updatedRoutes,
    selectedRoute: state.selectedRoute?.id === route_id ? null : state.selectedRoute,
    error: null,
  };
};

const deleteRouteFailureHandler = (state, action) => {
  const error = action.payload?.response?.data?.error || action.payload?.data?.error || 'Failed to delete route';

  createNotification('error', error, 3000);

  return {
    ...state,
    isDeleting: false,
    error: error,
  };
};

const optimizeFromLocationHandler = (state) => {
  return {
    ...state,
    isOptimizing: true,
    error: null,
  };
};

const optimizeFromLocationSuccessHandler = (state, action) => {
  const { data } = action.payload;

  createNotification('success', 'Route optimized from location successfully!', 3000);

  return {
    ...state,
    isOptimizing: false,
    optimizationResult: data,
    error: null,
  };
};

const optimizeFromLocationFailureHandler = (state, action) => {
  const error = action.payload?.error || action.payload?.response?.data?.error || action.payload?.data?.error || 'Optimization failed';

  createNotification('error', error, 3000);

  return {
    ...state,
    isOptimizing: false,
    error: error,
  };
};

const clearRouteOptimizationStateHandler = () => {
  return initialState;
};

const setSelectedRouteHandler = (state, action) => {
  return {
    ...state,
    selectedRoute: action.payload,
  };
};

const clearSelectedRouteHandler = (state) => {
  return {
    ...state,
    selectedRoute: null,
  };
};

// Action handlers map
const ACTION_HANDLERS = {
  [OPTIMIZE_ROUTES]: optimizeRoutesHandler,
  [OPTIMIZE_ROUTES_SUCCESS]: optimizeRoutesSuccessHandler,
  [OPTIMIZE_ROUTES_FAILURE]: optimizeRoutesFailureHandler,

  [GET_TASK_STATUS]: getTaskStatusHandler,
  [GET_TASK_STATUS_SUCCESS]: getTaskStatusSuccessHandler,
  [GET_TASK_STATUS_FAILURE]: getTaskStatusFailureHandler,

  [GET_ROUTES_BY_DATE]: getRoutesByDateHandler,
  [GET_ROUTES_BY_DATE_SUCCESS]: getRoutesByDateSuccessHandler,
  [GET_ROUTES_BY_DATE_FAILURE]: getRoutesByDateFailureHandler,

  [REORDER_ROUTE]: reorderRouteHandler,
  [REORDER_ROUTE_SUCCESS]: reorderRouteSuccessHandler,
  [REORDER_ROUTE_FAILURE]: reorderRouteFailureHandler,

  [INSERT_JOB_TO_ROUTE]: insertJobToRouteHandler,
  [INSERT_JOB_TO_ROUTE_SUCCESS]: insertJobToRouteSuccessHandler,
  [INSERT_JOB_TO_ROUTE_FAILURE]: insertJobToRouteFailureHandler,

  [UPDATE_JOB_LOCATION]: updateJobLocationHandler,
  [UPDATE_JOB_LOCATION_SUCCESS]: updateJobLocationSuccessHandler,
  [UPDATE_JOB_LOCATION_FAILURE]: updateJobLocationFailureHandler,

  [COMPARE_ROUTE_STRATEGIES]: compareRouteStrategiesHandler,
  [COMPARE_ROUTE_STRATEGIES_SUCCESS]: compareRouteStrategiesSuccessHandler,
  [COMPARE_ROUTE_STRATEGIES_FAILURE]: compareRouteStrategiesFailureHandler,

  [LOCK_ROUTE]: lockRouteHandler,
  [LOCK_ROUTE_SUCCESS]: lockRouteSuccessHandler,
  [LOCK_ROUTE_FAILURE]: lockRouteFailureHandler,

  [DELETE_ROUTE]: deleteRouteHandler,
  [DELETE_ROUTE_SUCCESS]: deleteRouteSuccessHandler,
  [DELETE_ROUTE_FAILURE]: deleteRouteFailureHandler,

  [OPTIMIZE_FROM_LOCATION]: optimizeFromLocationHandler,
  [OPTIMIZE_FROM_LOCATION_SUCCESS]: optimizeFromLocationSuccessHandler,
  [OPTIMIZE_FROM_LOCATION_FAILURE]: optimizeFromLocationFailureHandler,

  [CLEAR_ROUTE_OPTIMIZATION_STATE]: clearRouteOptimizationStateHandler,
  [SET_SELECTED_ROUTE]: setSelectedRouteHandler,
  [CLEAR_SELECTED_ROUTE]: clearSelectedRouteHandler,
};

export default function routeOptimizationReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

